import { Controller } from "@hotwired/stimulus"

// A bottom sheet that opens from a trigger button. It is the second use of the
// `.fk-sheet*` classes (the first is the My Tries feedback drawer).
//
// Put data-controller="bottom-sheet" on an element that holds the trigger and the
// sheet. Targets:
//   trigger   - the button that opens the sheet (gets aria-expanded)
//   container - the wrapper that is `hidden` while the sheet is closed
//   overlay   - the backdrop
//   sheet     - the dialog panel (focus moves here, Tab stays inside)
//
// Closing: a close button (action `bottom-sheet#close`), the overlay or Escape.
// Focus goes back to the trigger. The page scroll is locked only while open, and
// the rest of the page is `inert` (not focusable, hidden from assistive tech) so
// the dialog really is modal. Sends `bottom-sheet:opened` when the sheet opens.
const CLOSE_DELAY_MS = 400
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export default class extends Controller {
  static targets = ["trigger", "container", "overlay", "sheet"]

  connect() {
    this.opened = false
    this.inertElements = []
    this.onKeydown = this.#handleKeydown.bind(this)
  }

  disconnect() {
    clearTimeout(this.closeTimer)
    cancelAnimationFrame(this.frame)
    document.removeEventListener("keydown", this.onKeydown)
    document.body.style.overflow = ""
    this.#releaseBackground()
  }

  open() {
    if (this.opened) return

    this.opened = true
    clearTimeout(this.closeTimer)
    this.containerTarget.hidden = false
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", this.onKeydown)
    if (this.hasTriggerTarget) this.triggerTarget.setAttribute("aria-expanded", "true")
    this.#lockBackground()

    // Two frames, so the browser paints the closed state and the transition runs.
    // A close before then must cancel them, or they would re-open the classes.
    this.frame = requestAnimationFrame(() => {
      this.frame = requestAnimationFrame(() => {
        this.overlayTarget.classList.add("fk-sheet-overlay--open")
        this.sheetTarget.classList.add("fk-sheet--open")
      })
    })

    this.#focusFirst()
    this.dispatch("opened")
  }

  close() {
    if (!this.opened) return

    this.opened = false
    clearTimeout(this.closeTimer)
    cancelAnimationFrame(this.frame)
    document.removeEventListener("keydown", this.onKeydown)
    this.overlayTarget.classList.remove("fk-sheet-overlay--open")
    this.sheetTarget.classList.remove("fk-sheet--open")
    document.body.style.overflow = ""
    this.#releaseBackground()
    if (this.hasTriggerTarget) {
      this.triggerTarget.setAttribute("aria-expanded", "false")
      this.triggerTarget.focus()
    }
    this.closeTimer = setTimeout(() => { this.containerTarget.hidden = true }, CLOSE_DELAY_MS)
  }

  closeOnOverlay(event) {
    if (event.target === event.currentTarget) this.close()
  }

  // Everything in the controller element except the sheet container. Elements
  // that were already inert stay as they are when we release.
  #lockBackground() {
    this.inertElements = Array.from(this.element.children).filter((child) => {
      return child !== this.containerTarget && !child.hasAttribute("inert")
    })
    this.inertElements.forEach((child) => child.setAttribute("inert", ""))
  }

  #releaseBackground() {
    this.inertElements.forEach((child) => child.removeAttribute("inert"))
    this.inertElements = []
  }

  #handleKeydown(event) {
    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "Tab") {
      this.#trapFocus(event)
    }
  }

  #focusables() {
    return Array.from(this.sheetTarget.querySelectorAll(FOCUSABLE))
  }

  // Focus the dialog panel itself (it is `tabindex="-1"`), not its first focusable
  // descendant: content-only sheets have no field worth landing on, so jumping to
  // one (e.g. the bottom dismiss button) scrolls the title out of view and reads
  // the wrong thing to screen readers. The panel's `aria-labelledby` announces the
  // title either way.
  #focusFirst() {
    this.sheetTarget.focus()
  }

  #trapFocus(event) {
    const items = this.#focusables()
    if (items.length === 0) {
      event.preventDefault()
      this.sheetTarget.focus()
      return
    }

    const first = items[0]
    const last = items[items.length - 1]
    if (event.shiftKey && (document.activeElement === first || document.activeElement === this.sheetTarget)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    } else if (!this.sheetTarget.contains(document.activeElement)) {
      event.preventDefault()
      first.focus()
    }
  }
}
